import React from 'react';
import {
  Spade, Diamond, Club, Heart, Plane, Briefcase, Handshake, ClipboardList, Mic, Hotel,
  Gem, Cake, GraduationCap, Baby, Flame, Wine, Trophy, Music, ShoppingBag, Leaf,
  PartyPopper, Tent, Sparkles as SparkleIcon, Film, Users,
} from 'lucide-react';

/**
 * Shared data: 4 USE-CASE GROUPS for the Driven concierge service.
 * Consumed by:
 *   - /driven        (DrivenServices.jsx) — teaser cards only
 *   - /driven/occasions (DrivenOccasions.jsx) — full deep-dive page
 */export const USE_GROUPS = [
  {
    key: 'business',
    num: '01',
    label: 'Business & Executive',
    icon: Spade,
    title: <>Work Happens<br /><em className="not-italic text-red-500">Between the Meetings</em></>,
    desc: 'Your time in the car is billable. Make it count. ACE executive transport turns transit into a mobile command center.',
    cards: [
      { icon: Plane, tag: 'Airport Service', title: 'Airport Transfers', desc: 'Curbside pickup, terminal drop-off, flight monitoring, and meet-and-greet. No parking. No stress. Just wheels when you land.', vehicles: 'Sedan · SUV · Sprinter' },
      { icon: Briefcase, tag: 'Corporate', title: 'Executive Daily Rides', desc: 'Standing reservations for your leadership team. Door-to-door with privacy, punctuality, and an environment built for focus.', vehicles: 'Sedan · Luxury SUV' },
      { icon: Handshake, tag: 'Client Relations', title: 'Client & VIP Hosting', desc: 'Pick up your most important client in a vehicle that says everything before you say a word. First impressions are our business.', vehicles: 'Luxury SUV · Sprinter Lounge' },
      { icon: ClipboardList, tag: 'Multi-Stop', title: 'Roadshow & Site Visits', desc: 'Multiple locations, multiple stakeholders, one seamless day. ACE coordinates the route so your team stays together and on time.', vehicles: 'SUV · Sprinter' },
      { icon: Mic, tag: 'Events', title: 'Conference & Convention Transport', desc: 'Shuttle your team and guests between hotel, venue, and dinners. Group bookings handled with fleet-level precision.', vehicles: 'Sprinter · Multi-Vehicle Fleet' },
      { icon: Hotel, tag: 'Corporate Account', title: 'Hotel-to-HQ Runs', desc: 'Recurring routes for visiting partners, board members, or remote executives staying in the city. Consistent, reliable, branded.', vehicles: 'Sedan · SUV' },
    ],
  },
  {
    key: 'life-moments',
    num: '02',
    label: 'Life Moments',
    icon: Heart,
    title: <>The Occasions<br /><em className="not-italic text-red-500">That Deserve More</em></>,
    desc: 'Some days only come around once. ACE makes sure the ride matches the moment — before the photos, during the celebration, and all the way home.',
    cards: [
      { icon: Gem, tag: 'Weddings', title: 'Wedding Day Transport', desc: 'Bridal party arrivals, guest shuttles, getaway cars, and day-of coordination. Every vehicle dressed for the occasion, every run on time.', vehicles: 'Luxury SUV · Sprinter · Fleet' },
      { icon: Cake, tag: 'Celebrations', title: 'Birthday Experiences', desc: 'Bar crawls, dinner hops, surprise arrivals — the birthday person never touches a wheel. The group travels together. Everyone arrives safe.', vehicles: 'Sprinter Party · SUV' },
      { icon: GraduationCap, tag: 'Milestones', title: 'Graduation Celebrations', desc: 'Ceremony arrival, family dinners, after-parties. Mark the milestone with a ride the grad will remember as long as the degree.', vehicles: 'SUV · Sprinter' },
      { icon: Baby, tag: 'New Arrivals', title: 'Hospital & Baby Shower Runs', desc: 'Safe, clean, and calm transport when every detail matters most. From shower to hospital to home — ACE has you covered.', vehicles: 'Sedan · Luxury SUV' },
      { icon: Flame, tag: 'Remembrance', title: 'Funeral & Repast Transport', desc: 'Discreet, dignified, and dependable. Family and guests handled with professionalism during the days that demand the most grace.', vehicles: 'Sedan · SUV · Sprinter' },
      { icon: Heart, tag: 'Date Night', title: 'Romantic Evenings Out', desc: 'Reservation at seven, drop-off in style, pickup when you\'re ready. No valet anxiety. No parking garage. Just you two — fully present.', vehicles: 'Sedan · Luxury SUV' },
    ],
  },
  {
    key: 'lifestyle',
    num: '03',
    label: 'Lifestyle & Leisure',
    icon: Diamond,
    title: <>The Good Life<br /><em className="not-italic text-red-500">Is Better Chauffeured</em></>,
    desc: 'Wine country, concert nights, sporting events, spa days — when the destination is the experience, the ride should be too.',
    cards: [
      { icon: Wine, tag: 'Wine & Spirits', title: 'Wine & Distillery Tours', desc: 'Taste through your itinerary without a designated driver holding the group back. ACE connects the tastings while you enjoy every pour.', vehicles: 'Sprinter · SUV' },
      { icon: Trophy, tag: 'Game Day', title: 'Sports & Stadium Events', desc: 'Tailgate or arrive at the gate — ACE handles event traffic so your pregame stays fun, not frustrating.', vehicles: 'Sprinter · Multi-SUV' },
      { icon: Music, tag: 'Entertainment', title: 'Concerts & Live Shows', desc: 'Pre-show dinner. Venue arrival. Late-night pickup. ACE runs the whole night so you can focus on the music, not the map.', vehicles: 'Sedan · SUV · Sprinter' },
      { icon: Trophy, tag: 'Recreation', title: 'Golf Club & Country Club', desc: 'Clubs in the trunk, associates on board. Arrive fresh off the highway and walk straight to the first tee — no hustle, no sweat.', vehicles: 'Luxury SUV · Sedan' },
      { icon: ShoppingBag, tag: 'Retail & Luxury', title: 'Personal Shopping Days', desc: 'Buckhead boutiques, Lenox, Phipps — the driver waits, the bags ride with you, and you cover twice the ground in half the time.', vehicles: 'SUV · Sedan' },
      { icon: Leaf, tag: 'Wellness', title: 'Spa & Wellness Retreats', desc: 'Door pickup. Serene arrival. Post-treatment ride home in total silence if you want it. The relaxation starts the moment you get in.', vehicles: 'Sedan · Luxury SUV' },
    ],
  },
  {
    key: 'group',
    num: '04',
    label: 'Group & Event Logistics',
    icon: Club,
    title: <>When the Whole<br /><em className="not-italic text-red-500">Crew Needs to Move</em></>,
    desc: 'Bachelorette weekends. Corporate retreats. Family reunions. When headcount grows, so does our fleet. ACE scales without skipping a beat.',
    cards: [
      { icon: PartyPopper, tag: 'Bachelorette', title: 'Bachelorette & Bachelor Experiences', desc: 'The whole party, one vehicle. Multiple stops, zero drama. Champagne, playlists, and a driver who keeps the night moving forward.', vehicles: 'Sprinter Lounge · Multi-SUV' },
      { icon: Tent, tag: 'Retreats', title: 'Corporate Retreat Shuttles', desc: 'Hotel pickups, team dinners, offsite venue transfers. ACE keeps your entire leadership team connected across the full retreat schedule.', vehicles: 'Sprinter · Multi-Vehicle' },
      { icon: GraduationCap, tag: 'School', title: 'Prom & Senior Events', desc: 'Safe, stylish, and stress-free for parents. The group arrives together, stays together, and comes home when you say so.', vehicles: 'SUV · Sprinter' },
      { icon: SparkleIcon, tag: 'VIP Events', title: 'Galas & Charity Fundraisers', desc: 'Black-tie arrivals. Valet coordination. Guest transport from valet to door. ACE brings the professionalism the event demands.', vehicles: 'Luxury SUV · Sedan · Fleet' },
      { icon: Users, tag: 'Family', title: 'Family Reunions & Gatherings', desc: 'Airport runs, venue shuttles, hotel loops — keep the family together without anyone getting lost or making the wrong turn.', vehicles: 'SUV · Sprinter · Fleet' },
      { icon: Film, tag: 'Film & Media', title: 'Production & Talent Transport', desc: 'Set shuttles, talent arrivals, crew movements. ACE handles entertainment production logistics with precision and discretion.', vehicles: 'SUV · Sprinter · Multi-Vehicle' },
    ],
  },
];

/**
 * Flat list of every supported occasion for the deep-dive Occasions page.
 */
export const OCCASIONS = [
  { name: 'Airport Arrivals & Departures', cats: ['transport'] },
  { name: 'Anniversary Dinners', cats: ['celebration'] },
  { name: 'Award Shows & Premieres', cats: ['lifestyle'] },
  { name: 'Baby Showers', cats: ['celebration'] },
  { name: 'Bachelor Parties', cats: ['group', 'celebration'] },
  { name: 'Bachelorette Weekends', cats: ['group', 'celebration'] },
  { name: 'Birthday Celebrations', cats: ['celebration'] },
  { name: 'Black-Tie Galas', cats: ['lifestyle', 'group'] },
  { name: 'Board Member Visits', cats: ['business'] },
  { name: 'Charity Fundraisers', cats: ['group', 'lifestyle'] },
  { name: 'Client Entertainment', cats: ['business'] },
  { name: 'Concert Nights', cats: ['lifestyle'] },
  { name: 'Conference Shuttles', cats: ['business', 'group'] },
  { name: 'Convention Transport', cats: ['business', 'group'] },
  { name: 'Corporate Retreats', cats: ['business', 'group'] },
  { name: 'Date Nights', cats: ['lifestyle', 'celebration'] },
  { name: 'Distillery Tours', cats: ['lifestyle', 'group'] },
  { name: 'Executive Airport Runs', cats: ['business', 'transport'] },
  { name: 'Family Reunions', cats: ['group', 'celebration'] },
  { name: 'Fashion Shows', cats: ['lifestyle'] },
  { name: 'Film & TV Production', cats: ['production', 'business'] },
  { name: 'Funeral & Repast', cats: ['transport'] },
  { name: 'Golf Outings', cats: ['lifestyle'] },
  { name: 'Graduation Day', cats: ['celebration'] },
  { name: 'Holiday Party Nights', cats: ['group', 'celebration'] },
  { name: 'Hospital Runs', cats: ['transport'] },
  { name: 'Hotel Transfers', cats: ['transport', 'business'] },
  { name: 'Investor & Pitch Meetings', cats: ['business'] },
  { name: 'Luxury Shopping Days', cats: ['lifestyle'] },
  { name: 'Major Sporting Events', cats: ['lifestyle', 'group'] },
  { name: 'Music Video Shoots', cats: ['production', 'lifestyle'] },
  { name: "New Year's Eve", cats: ['celebration', 'group'] },
  { name: 'Nightlife & Bar Crawls', cats: ['lifestyle', 'group'] },
  { name: 'Photoshoots & Editorials', cats: ['production', 'lifestyle'] },
  { name: 'Prom Night', cats: ['celebration', 'group'] },
  { name: 'Private Aviation Pickup', cats: ['transport', 'business'] },
  { name: 'Quinceañera & Sweet 16', cats: ['celebration', 'group'] },
  { name: 'Real Estate Showings', cats: ['business', 'lifestyle'] },
  { name: 'Religious Ceremonies', cats: ['celebration', 'transport'] },
  { name: 'Roadshows & Site Visits', cats: ['business'] },
  { name: 'Senior Assisted Transport', cats: ['transport'] },
  { name: 'Spa & Wellness Days', cats: ['lifestyle'] },
  { name: 'Sports Team Transport', cats: ['group', 'lifestyle'] },
  { name: 'Surprise Experiences', cats: ['celebration', 'lifestyle'] },
  { name: 'Talent & Artist Runs', cats: ['production', 'business'] },
  { name: "Valentine's Day", cats: ['celebration', 'lifestyle'] },
  { name: 'VIP Event Hosting', cats: ['group', 'business'] },
  { name: 'Wedding Day & Party', cats: ['celebration', 'group'] },
  { name: 'Wine Country Tours', cats: ['lifestyle', 'group'] },
  { name: 'Your Reason. Your Ride.', cats: ['all'] },
];

export const OCCASION_FILTERS = [
  { key: 'all', label: 'All Occasions' },
  { key: 'business', label: 'Business' },
  { key: 'celebration', label: 'Celebrations' },
  { key: 'lifestyle', label: 'Lifestyle' },
  { key: 'group', label: 'Group Events' },
  { key: 'transport', label: 'Transport' },
  { key: 'production', label: 'Production' },
];
